/**
 * The Resources screen's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/resources` (all optional; a default is never written):
 * - `q`: search on the resource name.
 * - `status`: the Active/Inactive/All filter (default `active`).
 * - `sort` + `dir`: the column sort. The server sends resources in no defined order, so
 *   the default is Name ascending. Locations (a list per row) is not sortable.
 */
import type { V2Resource } from '@/api/resources';
import { matchesSearch } from '@/components/list/listSearch';
import type { SortState, SortValue } from '@/components/list/listSort';

export const RESOURCES_URL_DEFAULTS = { q: '', status: 'active', sort: '', dir: '' };

export type ResourceSortKey = 'name' | 'capacity' | 'status';
export const RESOURCE_SORT_KEYS: readonly ResourceSortKey[] = ['name', 'capacity', 'status'];
export const RESOURCE_DEFAULT_SORT: SortState<ResourceSortKey> = { key: 'name', dir: 'asc' };

/** Display name for a resource (its name, falling back to the id). */
export function resourceName(resource: V2Resource): string {
  return resource.name.length > 0 ? resource.name : resource.id;
}

/** What a resource row shows (and sorts by) in each sortable column. */
export function resourceSortValue(resource: V2Resource, key: ResourceSortKey): SortValue {
  switch (key) {
    case 'name':
      return resourceName(resource);
    case 'capacity':
      return resource.capacity;
    case 'status':
      return resource.isActive ? 'Active' : 'Inactive';
  }
}

/** The Resources search: the resource name. */
export function resourceMatches(resource: V2Resource, query: string): boolean {
  return matchesSearch(query, [resourceName(resource)]);
}
